/**
 * @module pacto
 *
 * Pacto del grupo — modalidad pozo/honor, votación con firmas,
 * certificación, sistema de sugerencias, delegación de control de edición.
 *
 * FASE 4 · F1.3.c1: `pacto`, `firmas_pacto` y `propuestas_pacto` viven en
 * Supabase Postgres (cierra DT-AD). `renderPacto` es ASYNC: pinta un estado
 * de carga, lee con `getPactoByGroup()` y recién ahí pinta la vista. Las
 * mutaciones (guardar / firmar / certificar / tomar control / delegar /
 * propuestas) pasan por `src/core/pacto.js` — RPCs `SECURITY DEFINER` para el
 * pacto, CRUD directo para las propuestas. Cada handler hace `await` + re-render.
 *
 * Estados del pacto (Schema §4 · F1.3.c1): 'definir' | 'firmar' | 'certificado'.
 * 'certificado' es el estado final bloqueado. El paso intermedio "Certificar"
 * (umbral de firmas alcanzado) es DERIVADO en el cliente — no persiste.
 *
 * Identidad: `sesion_${codigo}` (localStorage, lo setea grupo.js) guarda el
 * `user_id` del jugador activo. `editor_id` y las firmas son `player_id`.
 *
 * Deuda técnica conocida (no atacada en este bloque):
 *   - alert()/confirm() como UX (~12 usos) — CLAUDE.md §13. Migrar a modal
 *     propio en limpieza post-3.15.
 *   - Textos de UI ("PACTO ACTIVO", "quedará activo") siguen diciendo "activo";
 *     el estado canónico es "certificado" (DT-AI). Cosmético — fuera de scope §4.
 */

import { getDB } from '../core/db.js';
import { navigate } from '../core/router.js';
import { renderActiveGroupBar } from '../ui/components.js';
import { appRoot } from '../utils/dom.js';
import { formatNumber } from '../utils/format.js';
// Registra window.openReglamentoModal para el botón "Ver reglamento completo".
// Side-effect import (no named): un named sin uso lo tree-shakea rollup.
import '../ui/reglamento-modal.js';
// Idem: window.openCondicionesModal para "Responsabilidades y Condiciones de Uso".
import '../ui/condiciones-modal.js';
import { normalizarCodigoCupon } from '../utils/cupon.js';
import {
  getPactoByGroup,
  guardarPacto,
  firmarPacto,
  certificarPacto,
  validarCupon,
  tomarControlPacto,
  delegarPacto,
  crearPropuestaPacto,
  actualizarEstadoPropuesta,
  borrarPropuesta,
} from '../core/pacto.js';

// Config por defecto. El pacto recién creado guarda `config = {}` (estado
// 'definir'); el render lo normaliza con estos defaults para no mostrar
// `undefined` en los selects/resumen.
const DEFAULT_CONFIG = {
  aporte: 'Sin definir',
  aporteOtro: '',
  premioPozo: 'Sin definir',
  premioPozoOtro: '',
  premioHonor: 'Sin definir',
  premioHonorOtro: '',
  penalidad: 'Sin definir',
  penalidadOtro: '',
  // Anfitrión de extranjeros (modelo de negocio, aplicación posterior). Se
  // guarda como `ofrece_anfitrion_extranjeros` en config JSONB del pacto —
  // NOMBRE DISTINTO de la columna grupos.acepta_extranjeros (concepto del grupo,
  // cableado al cap de extranjeros) para no chocar conceptos. No la toca.
  // Default true = "Sí". Por ahora solo dato + texto informativo.
  ofrece_anfitrion_extranjeros: true,
};

// Traduce los errores crudos de los RPCs del pacto a mensajes de UX.
const traducirError = (error) => {
  const m = (error?.message || '').toLowerCase();
  if (m.includes('no_sos_editor')) return 'Solo el editor del pacto puede hacer este cambio.';
  if (m.includes('ya_certificado') || m.includes('pacto_certificado')) return 'El pacto ya está certificado y no se puede modificar.';
  if (m.includes('umbral_no_alcanzado')) return 'Todavía no se alcanzó el umbral de firmas para certificar.';
  if (m.includes('editor_activo')) return 'El editor sigue activo: todavía no se puede tomar el control.';
  if (m.includes('pacto_sin_contenido')) return 'Falta definir el contenido del pacto antes de firmar.';
  if (m.includes('no_sos_miembro')) return 'No sos miembro de este grupo.';
  if (m.includes('editor_invalido')) return 'No se puede delegar la edición a ese jugador.';
  if (m.includes('tipo_invalido')) return 'La modalidad del pacto no es válida.';
  if (m.includes('no_autenticado')) return 'Tu sesión expiró. Recargá la página.';
  if (m.includes('cupon_invalido')) return 'El cupón no es válido.';
  return 'No pudimos completar la acción. Probá de nuevo.';
};

// C3: motivo de validar_cupon (read-only) → mensaje de UX.
// Código conocido → traducción de siempre. Motivo nuevo que ya viene como texto
// (ej. compat anfitrión §18.12, que el backend manda listo para mostrar) → tal
// cual. Vacío/desconocido sin texto → genérico.
export const traducirMotivoCupon = (motivo) => {
  const conocidos = {
    cupon_inexistente: 'Ese cupón no existe.',
    cupon_vencido: 'El cupón está vencido.',
    cupon_sin_usos: 'El cupón ya no tiene usos disponibles.',
    cupon_ya_canjeado: 'Tu grupo ya usó este cupón.',
  };
  if (conocidos[motivo]) return conocidos[motivo];
  if (typeof motivo === 'string' && motivo.trim()) return motivo;
  return 'El cupón no es válido.';
};

const LOADING_HTML = `
    <div class="view-enter" style="width: 100%; padding: 4rem 1rem; text-align: center; color: var(--text-muted);">
        Cargando el pacto…
    </div>
`;

const errorHtml = (msg) => `
    <div class="view-enter" style="width: 100%; padding: 3rem 1rem; text-align: center;">
        <p style="color: #ff9d9d; font-size: 0.95rem;">${msg}</p>
        <button class="btn btn-secondary" style="margin-top: 1rem; width: auto;" onclick="window.location.reload()">Reintentar</button>
    </div>
`;

export const renderPacto = async (codigo) => {
    const db = getDB();
    const grupo = db.grupos[codigo];
    if (!grupo) { navigate('/ingreso'); return; }

    // Jugador activo: `sesion_${codigo}` guarda el user_id (lo setea grupo.js).
    const sesionActiva = localStorage.getItem(`sesion_${codigo}`);
    const jugadorActivo = (grupo.jugadores || []).find(j => j.user_id === sesionActiva);
    if (!jugadorActivo) { navigate(`/g/${codigo}`); return; }

    // Estado de carga mientras se resuelve la lectura contra Postgres.
    appRoot.innerHTML = LOADING_HTML;

    // Lectura. Si el grupo todavía no tiene pacto, se auto-crea con config
    // vacía (estado 'definir'); el caller queda como editor (guardar_pacto).
    // Si otra sesión lo creó primero, el INSERT devuelve 'no_sos_editor' — se
    // ignora y se relee. DT-AH: el editor es quien abre /pacto primero, no
    // necesariamente el creador del grupo (es_editor_pacto).
    let pacto = await getPactoByGroup(grupo.group_id);
    if (!pacto) {
        const r = await guardarPacto(grupo.group_id, `Pacto - ${grupo.nombre}`, 'pozo', {});
        if (r.error && !(r.error.message || '').toLowerCase().includes('no_sos_editor')) {
            appRoot.innerHTML = errorHtml(`No pudimos crear el pacto. ${traducirError(r.error)}`);
            return;
        }
        pacto = await getPactoByGroup(grupo.group_id);
    }
    if (!pacto) {
        appRoot.innerHTML = errorHtml('No pudimos cargar el pacto del grupo.');
        return;
    }

    // `pacto.config` viene con el nombre embebido (lo embebe getPactoByGroup).
    // Se separa: `rawConfig` = config jsonb cruda (sin nombre); `config` =
    // normalizada con defaults para el render; `nombrePacto` aparte.
    const { nombre: _nombreEmbebido, ...rawConfig } = pacto.config || {};
    const config = { ...DEFAULT_CONFIG, ...rawConfig };
    const nombrePacto = pacto.nombre || `Pacto - ${grupo.nombre}`;

    const isEditor = pacto.editor_id === jugadorActivo.id;
    const isActivo = pacto.estado === 'certificado';
    const canEdit = isEditor && !isActivo;
    const miFirma = pacto.firmas.includes(jugadorActivo.id);

    // Lógica de firmas.
    const totalMiembros = grupo.jugadores.length;
    const firmasCount = pacto.firmas.length;
    const umbral = Math.ceil(totalMiembros / 2);
    const canCertify = firmasCount >= umbral;

    // Resumen de premios/penalidad/aporte — calculado a nivel de función; lo usa
    // la vista de resumen (no-editor) para mostrar los valores del pacto.
    let premioStr = pacto.tipo === 'pozo' ? config.premioPozo : config.premioHonor;
    if (pacto.tipo === 'pozo' && config.premioPozo === 'Personalizado') premioStr = config.premioPozoOtro || 'Personalizado';
    if (pacto.tipo === 'honor' && config.premioHonor !== 'Camiseta oficial de la selección') premioStr = config.premioHonorOtro ? `${config.premioHonor} ${config.premioHonorOtro}` : config.premioHonor;
    const penalidadStr = config.penalidad === 'Otro castigo' ? config.penalidadOtro : config.penalidad;
    const aporteStr = config.aporte === 'otro' ? config.aporteOtro : config.aporte;

    // Edición de config: el cambio de nombre conserva la config cruda (no sube
    // versión); el cambio de contenido manda la config normalizada (sube
    // versión y resetea firmas — todo server-side en guardar_pacto).
    const handleEditConfig = async (key, value) => {
        if (!canEdit) return;
        const r = key === 'nombre'
            ? await guardarPacto(grupo.group_id, value, pacto.tipo, rawConfig)
            : await guardarPacto(grupo.group_id, nombrePacto, pacto.tipo, { ...config, [key]: value });
        if (r.error) { alert('⚠️ ' + traducirError(r.error)); return; }
        // Persistimos SIN re-renderizar toda la pantalla (evita el parpadeo del
        // LOADING + remonta completo). El estado local se mantiene en sync para
        // que un re-pintado parcial (cambio de modalidad) refleje el último valor.
        if (key !== 'nombre') { config[key] = value; rawConfig[key] = value; }
    };

    const firmasAvatars = grupo.jugadores.filter(j => pacto.firmas.includes(j.id)).map(j => `
        <div title="${j.apodo}" onclick="alert('✅ Ya firmó: ${j.apodo}')" style="cursor:pointer; width: 26px; height: 26px; border-radius: 50%; background: var(--celeste); color: #000; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: bold; border: 2px solid var(--glass-bg); margin-left: -6px;">
            ${j.apodo[0].toUpperCase()}
        </div>
    `).join('');

    const firmasInlineHtml = `
        <div style="margin-top: 0.4rem;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
                <div>
                    <strong style="color: var(--white); font-size: 0.95rem; display: block;">Firmas del pacto</strong>
                    <span style="font-size: 0.8rem; color: var(--text-muted);">${isActivo ? 'Certificado y bloqueado' : (canCertify ? 'Listo para certificar' : `Faltan ${umbral - firmasCount} firmas`)}</span>
                </div>
                <div style="display: flex; padding-left: 6px;">
                    ${firmasAvatars || '<span style="font-size: 0.8rem; color: var(--text-muted);">0 firmas</span>'}
                </div>
            </div>

            ${!isActivo ? `
                ${miFirma ? `
                    <button class="btn" style="background: rgba(255,255,255,0.05); color: var(--celeste); width: 100%; border: 1px solid var(--glass-border); cursor: default; margin-bottom: 0.5rem;">✅ Ya firmaste el pacto</button>
                ` : `
                    <button id="btnFirmar" class="btn btn-primary" style="margin-bottom: 0.5rem; background: var(--celeste); color: #000; font-weight: bold; width: 100%;">Firmar Pacto</button>
                `}

                ${canCertify ? `
                    <button id="btnCertificar" class="btn" style="width: 100%; margin-bottom: 0.5rem; background: #4caf50; color: #fff; font-weight: bold; border: none;">
                        Certificar Pacto
                    </button>
                ` : ''}

                <p style="font-size: 0.75rem; color: var(--text-muted); margin: 0.2rem 0 0.55rem; line-height: 1.35;">Con la firma de la mayoría ya se puede certificar. La certificación es con pago (equivalente a 2 kg de asado) o con cupones de regalo.</p>
                <button id="btnBeneficiosCert" class="btn btn-secondary" style="width: 100%; margin: 0; padding: 0.55rem; font-size: 0.8rem; border: 1px solid rgba(255,255,255,0.2);">Beneficios de certificación</button>
            ` : `
                ${miFirma ? `
                    <button class="btn" style="background: rgba(255,255,255,0.05); color: var(--celeste); width: 100%; border: 1px solid var(--glass-border); cursor: default; margin-bottom: 0.5rem;">✅ Ya estás adherido al pacto</button>
                ` : `
                    <button id="btnFirmar" class="btn btn-primary" style="margin-bottom: 0.5rem; background: #4caf50; color: #fff; font-weight: bold; border: none; width: 100%;">Adherirme al Pacto</button>
                `}
            `}

            <p style="font-size: 0.72rem; color: var(--text-muted); text-align: center; margin: 0.45rem 0 0; line-height: 1.35; opacity: 0.85;">La firma del pacto implica la aceptación del Reglamento y las Condiciones de Uso de PACTO MUNDIAL.</p>

            <!-- Modal informativo "Beneficios de certificar" — patrón overlay glass
                 + ✕ + tap-afuera (mismo que jugadores-modal). Solo informativo. -->
            <div id="modalBeneficiosCert" style="display:none; position:fixed; inset:0; z-index:90; background:rgba(0,0,0,0.66); align-items:center; justify-content:center; padding:1rem;">
                <div class="glass-card" style="position:relative; width:min(100%,420px); max-height:calc(100dvh - 2rem); overflow-y:auto; padding:1.3rem 1.1rem 1.1rem; border-radius:14px; background:radial-gradient(circle at 14% 0%, rgba(117,170,219,.10), transparent 38%), rgba(12,18,30,.98); border:1px solid rgba(117,170,219,.2);">
                    <button id="btnCerrarBeneficios" type="button" aria-label="Cerrar" style="position:absolute; top:.55rem; right:.65rem; width:30px; height:30px; border-radius:999px; border:1px solid rgba(255,255,255,.14); background:rgba(255,255,255,.05); color:var(--white); font-size:.85rem; cursor:pointer;">✕</button>
                    <h2 style="margin:0 1.8rem .6rem 0; color:var(--white); font-family:var(--font-heading); font-size:1.1rem; line-height:1.15;">Beneficios de certificar</h2>
                    <p style="margin:0; color:var(--text-muted); font-size:.9rem; line-height:1.4;">Certificar el pacto garantiza la visualización del ranking del grupo y permite subir los rituales de tu grupo de amigos a la tribuna pública.</p>
                </div>
            </div>
        </div>
    `;

    // Render components
    let configuracionHtml = '';
    // Definidos dentro del bloque canEdit; declarados acá para que el wiring de
    // listeners (más abajo) pueda re-pintar SOLO las secciones al cambiar de
    // modalidad, sin re-montar la pantalla.
    let seccionesHtmlFor = () => '';
    let wireSeccionHandlers = () => {};

    // Control cuadrado tildable (Sí/No) del anfitrión de extranjeros. Excluyente,
    // siempre uno activo. `editable` → botón clickeable (editor); si no, lectura
    // (no-editor). Tokens: --celeste (activo), --glass-border, --white, --bg-dark
    // (tilde sobre celeste); variantes de opacidad del glass. Sin hex nuevos.
    const anfitrionOpt = (val, label, activo, editable) => {
        const cap = val === 'si' ? 'Si' : 'No';
        const box = `<span ${editable ? `id="pactoAnfitrion${cap}Box" ` : ''}style="width:18px;height:18px;flex:0 0 auto;border-radius:5px;border:2px solid ${activo ? 'var(--celeste)' : 'rgba(255,255,255,0.3)'};background:${activo ? 'var(--celeste)' : 'transparent'};display:inline-flex;align-items:center;justify-content:center;color:var(--bg-dark);font-size:0.72rem;font-weight:900;">${activo ? '✓' : ''}</span>`;
        const sty = `flex:1;display:flex;align-items:center;justify-content:center;gap:0.5rem;min-height:44px;padding:0.5rem 0.7rem;border-radius:10px;background:${activo ? 'rgba(117,170,219,0.14)' : 'rgba(255,255,255,0.04)'};border:1px solid ${activo ? 'var(--celeste)' : 'var(--glass-border)'};color:var(--white);font-size:0.9rem;font-weight:800;`;
        return editable
            ? `<button type="button" id="pactoAnfitrion${cap}" class="pacto-anfitrion-opt" data-anfitrion="${val}" aria-pressed="${activo ? 'true' : 'false'}" style="${sty}cursor:pointer;">${box}${label}</button>`
            : `<div role="img" aria-label="${label}${activo ? ' seleccionado' : ''}" style="${sty}cursor:default;opacity:0.9;">${box}${label}</div>`;
    };

    const pozoAporteOptions = ['Sin definir', '10.000', '20.000', '50.000', '100.000', 'otro'];
    const pozoPremioOptions = ['Sin definir', 'Todo al ganador', '80% al ganador / 20% al segundo'];
    const honorPremioOptions = [
        'Sin definir',
        'Camiseta oficial de la selección',
        'No paga cancha de fútbol durante...',
        'No pone para el asado durante...',
        'Un viaje a...',
        'Una entrada para...',
        'Una caja de su bebida preferida...',
        'Otro premio'
    ];
    const penalidadOptions = [
        'Sin definir',
        'Paga el asado',
        'No puede opinar de fútbol por 1 año',
        'Foto con la camiseta rival',
        'Paga la cerveza y el fernet',
        'Hace las compras para la próxima juntada',
        'Va al arco el próximo partido',
        'Otro castigo'
    ];

    if (canEdit) {
        // Edit Form
        // Helper de sección: cada bloque del pacto en su propia tarjeta glass con
        // título acentuado (barra celeste a la izquierda). `alt` alterna sutilmente
        // la opacidad del fondo glass para dar ritmo entre secciones. Tokens:
        // --celeste, --celeste-glow, --white; los rgba(255,255,255,...) son
        // variantes de opacidad de --glass-bg (rgba(255,255,255,0.05)).
        const pactoSection = (titulo, contenido, alt = false) => `
            <div class="glass-card" style="padding: 1.1rem 1.15rem 1.25rem; margin-bottom: 1.1rem; background: ${alt ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.06)'}; border: 1px solid rgba(117,170,219,0.22);">
                <h3 style="display: flex; align-items: center; gap: 0.55rem; font-size: 0.95rem; color: var(--white); margin: 0 0 0.85rem; text-transform: uppercase; letter-spacing: 0.04em;">
                    <span style="flex: 0 0 auto; width: 3px; height: 1.05em; border-radius: 999px; background: var(--celeste); box-shadow: 0 0 8px var(--celeste-glow);"></span>
                    ${titulo}
                </h3>
                ${contenido}
            </div>
        `;

        // Cartel informativo (mismo texto que el resumen). "NO" en el rojo apagado
        // ya usado en el cartel del resumen (#ff6b6b); banner en la familia roja de
        // promesa incumplida (rgba(239,68,68,...)). Sin hex nuevos.
        const avisoPlataforma = `
            <div style="margin-top: 0.9rem; padding: 0.6rem 0.8rem; border-radius: 10px; background: rgba(239,68,68,0.06); border: 1px solid rgba(239,68,68,0.18); color: var(--text-muted); font-size: 0.74rem; line-height: 1.3;">
                La plataforma <strong style="color: #ff6b6b;">NO</strong> recauda dinero para pozos
            </div>
        `;

        const tipoOptions = `
            <select id="pactoTipo" class="input-group" style="width:100%; padding: 1rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border); margin-bottom: 0; font-size: 1.2rem; font-weight: bold; text-transform: uppercase;">
                <option value="pozo" ${pacto.tipo === 'pozo' ? 'selected' : ''}>Pozo</option>
                <option value="honor" ${pacto.tipo === 'honor' ? 'selected' : ''}>Pacto de Honor</option>
            </select>
        `;

        // Fila Editor + Delegar (cabecera de la sección Modalidad).
        const editorRow = `
            <div style="display: flex; justify-content: flex-end; align-items: center; margin-bottom: 0.7rem;">
                <div style="font-size: 0.8rem; background: rgba(255,255,255,0.05); padding: 0.2rem 0.6rem; border-radius: 4px;">
                    Editor: <strong style="color: var(--white);">${grupo.jugadores.find(j => j.id === pacto.editor_id)?.apodo || 'N/A'}</strong>
                    <a href="javascript:void(0)" id="toggleDelegar" style="color: var(--celeste); text-decoration: none; margin-left: 0.5rem;">Delegar</a>
                </div>
            </div>
            <div id="panelDelegar" style="display: none; margin-bottom: 1rem; padding: 1rem; background: rgba(0,0,0,0.3); border-radius: 8px;">
                <label style="font-size: 0.8rem; color: var(--text-muted); display: block; margin-bottom: 0.5rem;">Delegar edición a:</label>
                <div style="display: flex; gap: 0.5rem;">
                    <select id="inlineDelegarSelect" style="flex: 1; padding: 0.5rem; border-radius: 4px; background: rgba(255,255,255,0.1); color: white; border: 1px solid var(--glass-border);">
                        <option value="">Seleccionar...</option>
                        ${grupo.jugadores.filter(j => j.id !== jugadorActivo.id).map(j => `<option value="${j.id}">${j.apodo}</option>`).join('')}
                    </select>
                    <button id="btnInlineDelegarConfirm" class="btn btn-secondary" style="margin: 0; padding: 0.5rem 1rem; font-size: 0.8rem;">OK</button>
                </div>
            </div>
        `;

        // Secciones por modalidad como función de `tipo`: permite re-pintar SOLO
        // el host de secciones al cambiar Modalidad, sin re-montar la pantalla
        // (sin parpadeo). Los inputs "otro/detalle" se renderizan SIEMPRE y se
        // muestran/ocultan por display (toggle en el onchange, sin re-render).
        // Cada sección en su tarjeta glass con título acentuado; opacidad alterna.
        seccionesHtmlFor = (tipo) => {
            const penalidadInner = `
                <select id="pactoPenalidad" style="width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border); margin-bottom: 0.5rem; margin-top: 0;">
                    ${penalidadOptions.map(opt => `<option value="${opt}" ${config.penalidad === opt ? 'selected' : ''}>${opt}</option>`).join('')}
                </select>
                <input type="text" id="pactoPenalidadOtro" value="${config.penalidadOtro || ''}" placeholder="Describe el castigo" style="display:${config.penalidad === 'Otro castigo' ? '' : 'none'}; margin-bottom:0; width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.2); border: 1px solid var(--glass-border); color: white;">
            `;
            // Anfitrión de extranjeros (modelo de negocio, aplicación posterior).
            // Default "Sí" (config.ofrece_anfitrion_extranjeros !== false). Solo dato + texto.
            const ofrece = config.ofrece_anfitrion_extranjeros !== false; // default Sí
            const anfitrionInner = `
                <div style="display:flex;gap:0.6rem;">
                    ${anfitrionOpt('si', 'Sí', ofrece, true)}
                    ${anfitrionOpt('no', 'No', !ofrece, true)}
                </div>
                <p style="font-size: 0.8rem; color: var(--text-muted); margin: 0.6rem 0 0; line-height: 1.3;">Para CERTIFICAR con CUPONES es necesario que el grupo oficie de anfitrión de jugadores extranjeros</p>
            `;
            const anfitrionTitulo = 'Este grupo se ofrece como anfitrión de extranjeros';
            if (tipo === 'pozo') {
                const aporteInner = `
                    <select id="pactoAporte" style="width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border); margin-bottom: 0.5rem; margin-top: 0;">
                        ${pozoAporteOptions.map(opt => `<option value="${opt}" ${config.aporte === opt ? 'selected' : ''}>${opt === 'otro' ? 'Otro Importe' : opt}</option>`).join('')}
                    </select>
                    <input type="text" id="pactoAporteOtro" value="${config.aporteOtro || ''}" placeholder="Ingresa el monto" style="display:${config.aporte === 'otro' ? '' : 'none'}; margin-bottom:0; width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.2); border: 1px solid var(--glass-border); color: white;">
                `;
                const premioInner = `
                    <select id="pactoPremioPozo" style="width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border); margin-bottom: 0.5rem; margin-top: 0;">
                        ${pozoPremioOptions.map(opt => `<option value="${opt}" ${config.premioPozo === opt ? 'selected' : ''}>${opt}</option>`).join('')}
                    </select>
                    <p style="font-size: 0.8rem; color: var(--text-muted); margin: 0.2rem 0 0;">Si hay empate, se divide entre los que empatan.</p>
                `;
                return pactoSection('Aporte pesos argentinos por jugador', aporteInner, true)
                    + pactoSection('Premio a los ganadores', premioInner, false)
                    + pactoSection('Penalidad para el último', penalidadInner, true)
                    + pactoSection(anfitrionTitulo, anfitrionInner, false);
            }
            const premioInner = `
                <p style="font-size: 0.85rem; color: var(--text-muted); margin: 0 0 0.6rem; text-transform: uppercase;">todo el grupo se compromete</p>
                <select id="pactoPremioHonor" style="width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border); margin-bottom: 0.5rem;">
                    ${honorPremioOptions.map(opt => `<option value="${opt}" ${config.premioHonor === opt ? 'selected' : ''}>${opt}</option>`).join('')}
                </select>
                <input type="text" id="pactoPremioHonorOtro" value="${config.premioHonorOtro || ''}" placeholder="Detalle del premio (ej: 3 meses, Mendoza, Fernet Branca)" style="display:${config.premioHonor !== 'Camiseta oficial de la selección' ? '' : 'none'}; margin-bottom:0; width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.2); border: 1px solid var(--glass-border); color: white;">
            `;
            return pactoSection('Premio al ganador', premioInner, true)
                + pactoSection('Penalidad para el último', penalidadInner, false)
                + pactoSection(anfitrionTitulo, anfitrionInner, true);
        };

        // Wiring de los listeners de las secciones (selects + inputs "otro").
        // Reutilizado en el render inicial y tras re-pintar el host por cambio de
        // modalidad. Cada cambio PERSISTE (handleEditConfig) y togglea el input
        // dependiente por display — sin re-render de la pantalla (sin parpadeo).
        wireSeccionHandlers = () => {
            const aporte = document.getElementById('pactoAporte');
            if (aporte) aporte.addEventListener('change', (e) => {
                handleEditConfig('aporte', e.target.value);
                const otro = document.getElementById('pactoAporteOtro');
                if (otro) otro.style.display = e.target.value === 'otro' ? '' : 'none';
            });
            const aporteOtro = document.getElementById('pactoAporteOtro');
            if (aporteOtro) {
                aporteOtro.addEventListener('input', (e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    e.target.value = val ? formatNumber(val) : '';
                });
                aporteOtro.addEventListener('change', (e) => handleEditConfig('aporteOtro', e.target.value));
            }
            const premioPozo = document.getElementById('pactoPremioPozo');
            if (premioPozo) premioPozo.addEventListener('change', (e) => handleEditConfig('premioPozo', e.target.value));
            const premioHonor = document.getElementById('pactoPremioHonor');
            if (premioHonor) premioHonor.addEventListener('change', (e) => {
                handleEditConfig('premioHonor', e.target.value);
                const otro = document.getElementById('pactoPremioHonorOtro');
                if (otro) otro.style.display = e.target.value !== 'Camiseta oficial de la selección' ? '' : 'none';
            });
            const premioHonorOtro = document.getElementById('pactoPremioHonorOtro');
            if (premioHonorOtro) premioHonorOtro.addEventListener('change', (e) => handleEditConfig('premioHonorOtro', e.target.value));
            const penalidad = document.getElementById('pactoPenalidad');
            if (penalidad) penalidad.addEventListener('change', (e) => {
                handleEditConfig('penalidad', e.target.value);
                const otro = document.getElementById('pactoPenalidadOtro');
                if (otro) otro.style.display = e.target.value === 'Otro castigo' ? '' : 'none';
            });
            const penalidadOtro = document.getElementById('pactoPenalidadOtro');
            if (penalidadOtro) penalidadOtro.addEventListener('change', (e) => handleEditConfig('penalidadOtro', e.target.value));
            // Anfitrión: dos cuadraditos excluyentes. setAnfitrion repinta AMBOS
            // por display/atributos (sin re-render → sin parpadeo); siempre queda
            // exactamente uno tildado. handleEditConfig persiste el boolean.
            const setAnfitrion = (ofreceVal) => {
                const paint = (btnId, boxId, activo) => {
                    const btn = document.getElementById(btnId);
                    if (btn) {
                        btn.setAttribute('aria-pressed', activo ? 'true' : 'false');
                        btn.style.background = activo ? 'rgba(117,170,219,0.14)' : 'rgba(255,255,255,0.04)';
                        btn.style.borderColor = activo ? 'var(--celeste)' : 'var(--glass-border)';
                    }
                    const box = document.getElementById(boxId);
                    if (box) {
                        box.style.background = activo ? 'var(--celeste)' : 'transparent';
                        box.style.borderColor = activo ? 'var(--celeste)' : 'rgba(255,255,255,0.3)';
                        box.textContent = activo ? '✓' : '';
                    }
                };
                paint('pactoAnfitrionSi', 'pactoAnfitrionSiBox', ofreceVal);
                paint('pactoAnfitrionNo', 'pactoAnfitrionNoBox', !ofreceVal);
            };
            const optSi = document.getElementById('pactoAnfitrionSi');
            if (optSi) optSi.addEventListener('click', () => { handleEditConfig('ofrece_anfitrion_extranjeros', true); setAnfitrion(true); });
            const optNo = document.getElementById('pactoAnfitrionNo');
            if (optNo) optNo.addEventListener('click', () => { handleEditConfig('ofrece_anfitrion_extranjeros', false); setAnfitrion(false); });
        };

        configuracionHtml = `
            <div class="mb-4">
                ${pactoSection('Modalidad de Juego', editorRow + tipoOptions + avisoPlataforma, false)}
                <div id="pactoSeccionesHost">${seccionesHtmlFor(pacto.tipo)}</div>
                <div class="glass-card" style="padding: 1.25rem 1.5rem; background: rgba(0,0,0,0.2);">
                    ${firmasInlineHtml}
                </div>
            </div>
        `;
    } else {
        // Summary View (Synthetic Box)
        configuracionHtml = `
            <div style="background: rgba(117,170,219,0.1); border: 1px solid rgba(117,170,219,0.3); padding: 1.5rem; border-radius: 12px; margin-bottom: 1.5rem; box-shadow: 0 4px 15px rgba(0,0,0,0.2);">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
                    <h3 style="font-size: 1.2rem; color: var(--white); margin: 0; text-transform: uppercase;">Resumen del Pacto</h3>
                    <span style="font-size: 0.75rem; background: rgba(0,0,0,0.3); padding: 0.2rem 0.6rem; border-radius: 4px; color: var(--text-muted);">
                        Editor: ${grupo.jugadores.find(j => j.id === pacto.editor_id)?.apodo || 'N/A'}
                    </span>
                </div>

                <div style="display: flex; justify-content: space-between; border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 0.8rem; margin-bottom: 0.8rem;">
                    <span style="color: var(--text-muted); font-size: 0.9rem;">Modalidad:</span>
                    <strong style="color: var(--celeste); font-size: 1rem; text-transform: uppercase;">${pacto.tipo === 'pozo' ? 'Pozo' : 'Pacto de Honor'}</strong>
                </div>

                ${pacto.tipo === 'pozo' ? `
                    <div style="border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 0.8rem; margin-bottom: 0.8rem;">
                        <div style="display: flex; justify-content: space-between;">
                            <span style="color: var(--text-muted); font-size: 0.9rem;">Aporte por jugador:</span>
                            <strong style="color: var(--white); font-size: 1.1rem;">$ ${aporteStr}</strong>
                        </div>
                        ${isActivo ? `
                        <div style="font-size: 0.7rem; color: var(--text-muted); margin-top: 0.4rem; opacity: 0.8;">
                            La plataforma <strong style="color: #ff6b6b; font-size: 0.75rem;">NO</strong> recauda dinero para pozos
                        </div>
                        ` : ''}
                    </div>
                ` : ''}

                <div style="display: flex; flex-direction: column; border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 0.8rem; margin-bottom: 0.8rem;">
                    <span style="color: var(--text-muted); font-size: 0.9rem; margin-bottom: 0.2rem;">Premio al ganador:</span>
                    <strong style="color: var(--white); font-size: 1rem; line-height: 1.3;">${premioStr}</strong>
                </div>

                <div style="display: flex; flex-direction: column;">
                    <span style="color: var(--text-muted); font-size: 0.9rem; margin-bottom: 0.2rem;">Penalidad para el último:</span>
                    <span style="color: #ff6b6b; font-size: 1rem; line-height: 1.3;">${penalidadStr}</span>
                </div>

                <div style="display: flex; flex-direction: column; border-top: 1px solid rgba(255,255,255,0.1); margin-top: 0.8rem; padding-top: 0.8rem;">
                    <span style="color: var(--text-muted); font-size: 0.9rem; margin-bottom: 0.4rem;">Anfitrión de extranjeros:</span>
                    <div style="display:flex;gap:0.6rem;">
                        ${anfitrionOpt('si', 'Sí', config.ofrece_anfitrion_extranjeros !== false, false)}
                        ${anfitrionOpt('no', 'No', config.ofrece_anfitrion_extranjeros === false, false)}
                    </div>
                    <p style="font-size: 0.75rem; color: var(--text-muted); margin: 0.5rem 0 0; line-height: 1.3;">Para CERTIFICAR con CUPONES es necesario que el grupo oficie de anfitrión de jugadores extranjeros</p>
                </div>

                ${firmasInlineHtml}
            </div>
        `;
    }


    // Sugerencias
    const renderSugerenciaItem = (s, isPropia) => `
        <div style="background: rgba(255,255,255,0.05); padding: 1rem; border-radius: 8px; margin-bottom: 0.5rem;">
            <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="display: flex; align-items: center; gap: 0.5rem;">
                    <div style="width: 24px; height: 24px; background: var(--celeste); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.7rem; color: #000; font-weight: bold;">
                        ${(grupo.jugadores.find(j => j.id === s.user_id)?.apodo || 'U')[0].toUpperCase()}
                    </div>
                    <strong style="color: var(--celeste); font-size: 0.9rem;">${grupo.jugadores.find(j => j.id === s.user_id)?.apodo || 'Usuario'}</strong>
                </div>
                <span style="font-size: 0.7rem; color: var(--text-muted); background: rgba(0,0,0,0.3); padding: 0.1rem 0.4rem; border-radius: 4px;">${s.estado.toUpperCase()}</span>
            </div>
            <p style="font-size: 0.85rem; margin-bottom: 0; color: var(--white);">${s.texto}</p>
            ${isEditor && s.estado === 'pendiente' && !isActivo ? `
                <div style="display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.8rem;">
                    <button class="btn-sugerencia-aplicar" data-id="${s.id}" style="background: var(--celeste); color: #000; border: none; padding: 0.3rem 0.6rem; border-radius: 4px; font-size: 0.75rem; cursor: pointer; font-weight: bold;">Aplicar</button>
                    <button class="btn-sugerencia-ignorar" data-id="${s.id}" style="background: rgba(255,255,255,0.1); color: white; border: none; padding: 0.3rem 0.6rem; border-radius: 4px; font-size: 0.75rem; cursor: pointer;">Ignorar</button>
                </div>
            ` : ''}
            ${!isEditor && isPropia && s.estado === 'pendiente' && !isActivo ? `
                <div style="display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.8rem;">
                    <button class="btn-sugerencia-borrar" data-id="${s.id}" style="background: rgba(255,107,107,0.1); color: #ff6b6b; border: 1px solid #ff6b6b; padding: 0.3rem 0.6rem; border-radius: 4px; font-size: 0.75rem; cursor: pointer;">Borrar</button>
                </div>
            ` : ''}
        </div>
    `;

    let sugerenciasPropiasHtml = '';
    let sugerenciasGrupoHtml = '';

    if (isEditor) {
        sugerenciasPropiasHtml = pacto.sugerencias.map(s => renderSugerenciaItem(s, false)).join('');
    } else {
        const misSugerencias = pacto.sugerencias.filter(s => s.user_id === jugadorActivo.id);
        const otrasSugerencias = pacto.sugerencias.filter(s => s.user_id !== jugadorActivo.id);
        sugerenciasPropiasHtml = misSugerencias.map(s => renderSugerenciaItem(s, true)).join('');
        sugerenciasGrupoHtml = otrasSugerencias.map(s => renderSugerenciaItem(s, false)).join('');
    }

    // Delegar / Tomar control
    let controlHtml = '';
    const lastActivity = pacto.editor_last_activity_at ? new Date(pacto.editor_last_activity_at).getTime() : Date.now();
    const daysSinceLastActivity = (Date.now() - lastActivity) / (1000 * 60 * 60 * 24);

    if (!isActivo) {
        if (isEditor) {
            // Se movió la opción de delegar a la vista inline (toggleDelegar).
        } else if (daysSinceLastActivity >= 7) {
            controlHtml = `
                <div class="glass-card mb-4" style="padding: 1.5rem; border-color: rgba(255, 107, 107, 0.4); background: rgba(255,107,107,0.05);">
                    <h3 style="font-size: 1.1rem; color: #ff6b6b; margin-bottom: 0.5rem;">⚠️ Tomar control</h3>
                    <p style="font-size: 0.8rem; color: var(--text-muted); margin-bottom: 1rem;">El editor lleva más de 7 días inactivo.</p>
                    <button id="btnTomarControl" class="btn" style="background: #ff6b6b; color: white; border: none; font-weight: bold; width: 100%; margin: 0;">Tomar el control de edición</button>
                </div>
            `;
        }
    }

    appRoot.innerHTML = `
        <div class="view-enter" style="width: 100%; animation: fadeIn 0.4s ease-out forwards; padding-bottom: 5rem;">
            ${renderActiveGroupBar(codigo)}
            <!-- Header Nav -->
            <div style="display: flex; align-items: center; justify-content: flex-start; gap: 1rem; margin-bottom: 1.5rem;">
                <button class="btn" style="width: auto; padding: 0.6rem 1rem; background: var(--glass-bg); border: 1px solid var(--glass-border); border-radius: 12px; color: var(--white); margin: 0;" onclick="window.location.hash='/g/${codigo}'">
                    ← Volver
                </button>
                <div>
                    <h2 style="font-family: var(--font-heading); font-size: 1.2rem; line-height: 1.2;">${grupo.nombre}</h2>
                    <span style="display: inline-block; margin-top: 0.2rem; padding: 0.2rem 0.6rem; border-radius: 4px; font-size: 0.65rem; font-weight: 800; text-transform: uppercase; ${isActivo ? 'background: rgba(76,175,80,0.2); color: #4caf50; border: 1px solid #4caf50;' : 'background: rgba(255,215,0,0.2); color: #FFD700; border: 1px solid #FFD700;'}">
                        ${isActivo ? 'PACTO CERTIFICADO' : 'PACTO EN CONSTRUCCIÓN'}
                    </span>
                </div>
            </div>

            <!-- Nombre del Pacto -->
            ${canEdit && !isActivo ? `<input type="text" id="pactoNombre" value="${nombrePacto}" style="width: 100%; background: transparent; border: none; border-bottom: 2px solid var(--celeste); color: var(--white); font-family: var(--font-heading); font-size: 1.5rem; margin-bottom: 1.5rem; padding: 0.5rem 0; text-align: center;">` : `<h2 style="text-align: center; font-family: var(--font-heading); font-size: 1.5rem; margin-bottom: 1.5rem; margin-top: 1rem;">${nombrePacto}</h2>`}

            <!-- Configuracion e Inline Firmas -->
            ${configuracionHtml}

            <!-- Sugerencias del Grupo -->
            ${!isEditor && !isActivo && sugerenciasGrupoHtml ? `
                <div class="glass-card mb-4" style="padding: 1.5rem;">
                    <h3 style="font-size: 1.1rem; color: var(--white); margin-bottom: 1rem;">🗣️ Sugerencias del Grupo para el pacto</h3>
                    <div style="max-height: 250px; overflow-y: auto;">
                        ${sugerenciasGrupoHtml}
                    </div>
                </div>
            ` : ''}

            <!-- Sugerencias Propias -->
            ${!isActivo ? `
                <div class="glass-card mb-4" style="padding: 1.5rem;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
                        <h3 style="font-size: 1.1rem; color: var(--white); margin: 0;">${isEditor ? '💡 Sugerencias recibidas para el pacto' : '💡 Mis Sugerencias para el pacto'}</h3>
                    </div>

                    ${sugerenciasPropiasHtml ? `<div style="margin-bottom: 1rem; max-height: 250px; overflow-y: auto;">${sugerenciasPropiasHtml}</div>` : '<p style="font-size: 0.85rem; color: var(--text-muted); text-align: center; margin-bottom: 1rem; background: rgba(255,255,255,0.02); padding: 1rem; border-radius: 8px;">No hay sugerencias aún.</p>'}

                    ${!isEditor ? `
                    <div style="margin-top: 1rem; background: rgba(0,0,0,0.2); padding: 1rem; border-radius: 8px; border: 1px solid rgba(255,255,255,0.05);">
                        <h4 style="font-size: 0.85rem; color: var(--text-muted); margin-bottom: 0.5rem; margin-top: 0;">Nueva Sugerencia para el pacto</h4>
                        <select id="sugCategoria" style="width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border); margin-bottom: 0.5rem;">
                            <option value="">Seleccionar categoría...</option>
                            <option value="PREMIO_HONOR">Premio Pacto de Honor</option>
                            <option value="PREMIO_POZO">Premio Pozo</option>
                            <option value="PENALIDAD">Penalidad para el último</option>
                        </select>

                        <div id="sugBloqueHonor" style="display:none;">
                            <select id="sugValorPremioHonor" style="width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border); margin-bottom: 0.5rem;">
                                <option value="">Seleccionar premio de honor...</option>
                                ${honorPremioOptions.filter(o => o !== 'Sin definir').map(opt => `<option value="${opt}">${opt}</option>`).join('')}
                            </select>
                        </div>

                        <div id="sugBloquePozo" style="display:none;">
                            <select id="sugValorAporte" style="width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border); margin-bottom: 0.5rem;">
                                <option value="">Seleccionar aporte en pesos...</option>
                                ${pozoAporteOptions.filter(o => o !== 'Sin definir').map(opt => `<option value="${opt}">${opt}</option>`).join('')}
                            </select>
                            <input type="text" id="sugValorAporteOtro" placeholder="Ingresa el monto" style="display:none; width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.2); border: 1px solid var(--glass-border); color: white; margin-bottom: 0.5rem;">
                            <select id="sugValorPremioPozo" style="width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border); margin-bottom: 0.5rem;">
                                <option value="">Seleccionar reparto de pozo...</option>
                                ${pozoPremioOptions.filter(o => o !== 'Sin definir').map(opt => `<option value="${opt}">${opt}</option>`).join('')}
                            </select>
                        </div>

                        <div id="sugBloquePenalidad" style="display:none;">
                            <select id="sugValorPenalidad" style="width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border); margin-bottom: 0.5rem;">
                                <option value="">Seleccionar castigo...</option>
                                ${penalidadOptions.filter(o => o !== 'Sin definir').map(opt => `<option value="${opt}">${opt}</option>`).join('')}
                            </select>
                        </div>

                        <input type="text" id="sugTextoExtra" placeholder="Comentario adicional (opcional, aplicará a la opción que elijas 'Otro')" style="width:100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border); margin-bottom: 0.5rem;">
                        <button id="btnSugerir" class="btn btn-secondary" style="margin: 0; width: 100%; margin-top: 0.5rem;">Enviar Sugerencia</button>
                    </div>
                    ` : ''}
                </div>
            ` : ''}

            <!-- Control -->
            ${controlHtml}

            <!-- Sistema de Puntos -->
            <div class="glass-card mb-4" style="padding: 1.5rem;">
                <h3 style="font-size: 1.1rem; color: var(--white); margin-bottom: 1rem;">🎯 Sistema de Puntos</h3>
                <button class="btn btn-secondary" style="font-size: 0.85rem; padding: 0.6rem; margin: 0; width: 100%;" onclick="window.openReglamentoModal()">Ver reglamento completo</button>
                <button class="btn btn-secondary" style="font-size: 0.85rem; padding: 0.6rem; margin: 0.5rem 0 0; width: 100%;" onclick="window.openCondicionesModal()">Responsabilidades y Condiciones de Uso</button>
            </div>

        </div>
    `;

    // Event Listeners (Solo si no está activo y se puede editar)
    if (canEdit) {
        document.getElementById('pactoNombre')?.addEventListener('change', (e) => handleEditConfig('nombre', e.target.value));
        // Cambio de modalidad: persiste y re-pinta SOLO el host de secciones
        // (Aporte/Premio/Penalidad), sin re-montar la pantalla ni mostrar el
        // loading → sin parpadeo. Re-cablea los listeners de las nuevas secciones.
        document.getElementById('pactoTipo').addEventListener('change', async (e) => {
            const nuevoTipo = e.target.value;
            const r = await guardarPacto(grupo.group_id, nombrePacto, nuevoTipo, config);
            if (r.error) { alert('⚠️ ' + traducirError(r.error)); return; }
            pacto.tipo = nuevoTipo; // estado local en sync
            const host = document.getElementById('pactoSeccionesHost');
            if (host) { host.innerHTML = seccionesHtmlFor(nuevoTipo); wireSeccionHandlers(); }
        });
        // Selects/inputs de las secciones: persisten + togglean su input "otro"
        // por display, sin re-render (definido junto a seccionesHtmlFor).
        wireSeccionHandlers();
    }

    // Modal informativo "Beneficios de certificar" (abrir/cerrar — solo UI).
    const btnBeneficios = document.getElementById('btnBeneficiosCert');
    const modalBeneficios = document.getElementById('modalBeneficiosCert');
    if (btnBeneficios && modalBeneficios) {
        const cerrar = () => { modalBeneficios.style.display = 'none'; };
        btnBeneficios.addEventListener('click', () => { modalBeneficios.style.display = 'flex'; });
        modalBeneficios.addEventListener('click', (e) => { if (e.target === modalBeneficios) cerrar(); });
        document.getElementById('btnCerrarBeneficios')?.addEventListener('click', cerrar);
    }

    const btnFirmar = document.getElementById('btnFirmar');
    if (btnFirmar) {
        btnFirmar.addEventListener('click', async () => {
            if (pacto.tipo === 'pozo' && (config.aporte === 'Sin definir' || config.premioPozo === 'Sin definir')) {
                alert('⚠️ Faltan definir el aporte y/o el premio antes de poder firmar.');
                return;
            }
            if (pacto.tipo === 'honor' && config.premioHonor === 'Sin definir') {
                alert('⚠️ Falta definir el premio antes de poder firmar.');
                return;
            }
            if (config.penalidad === 'Sin definir') {
                alert('⚠️ Falta definir la penalidad para el último antes de poder firmar.');
                return;
            }

            const r = await firmarPacto(pacto.pacto_id);
            if (r.error) { alert('⚠️ ' + traducirError(r.error)); return; }
            await renderPacto(codigo);
        });
    }

    if (!isActivo) {

        // C3 — hoja de cobro. Overlay NUEVO: NO se mete en el .view-enter (que es
        // stacking context por animation:fadeIn → atraparía el modal, igual que
        // pasaba con el de arenga en 6607d8d). Se PORTA a document.body con
        // z-index:110 (cubre la bottom-nav z-100) y la .glass-card scrollea como
        // la de 8f7ff93 (max-height:85%, overflow-y:scroll, sin dvh/vh, sin
        // transform/filter/will-change en ancestros → es hijo directo de body).
        const cerrarHojaCobro = () => {
            document.querySelectorAll('body > [data-cobro-overlay]').forEach(n => n.remove());
        };
        const fmtMoneda = (centavos, moneda) =>
            new Intl.NumberFormat('es-AR', { style: 'currency', currency: moneda || 'ARS', maximumFractionDigits: 0 }).format(centavos / 100);

        const abrirHojaCobro = ({ orden_id, monto_final_centavos, moneda }) => {
            cerrarHojaCobro(); // sin duplicados
            const base = monto_final_centavos; // monto SIN cupón (del requiere_pago de descubrimiento)
            let cuponAplicado = null;
            const overlay = document.createElement('div');
            overlay.id = 'hojaCobroPacto';
            overlay.dataset.cobroOverlay = '1';
            overlay.style.cssText = 'position:fixed; inset:0; z-index:110; background:rgba(0,0,0,0.66); display:flex; align-items:flex-end; justify-content:center; padding:0.85rem;';
            overlay.innerHTML = `
                <div class="glass-card" style="width:min(100%,430px); max-height:85%; min-height:0; box-sizing:border-box; overflow-y:scroll; -webkit-overflow-scrolling:touch; overscroll-behavior:contain; padding:1.1rem 1.1rem 5rem; border-radius:16px 16px 12px 12px; border-color:rgba(76,175,80,.28); background:radial-gradient(circle at 18% 0%, rgba(76,175,80,.16), transparent 34%), rgba(12,18,30,.97);">
                    <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:.8rem; margin-bottom:.85rem;">
                        <div>
                            <h3 style="margin:0; color:var(--white); font-family:var(--font-heading); font-size:1.14rem;">Certificar el pacto</h3>
                            <span style="display:block; color:var(--text-muted); font-size:.78rem; margin-top:.18rem;">Pago único (equivalente a ~2 kg de asado).</span>
                        </div>
                        <button type="button" data-cobro-close="1" class="btn btn-secondary" style="width:auto; min-height:34px; margin:0; padding:.35rem .6rem; border-radius:999px;">X</button>
                    </div>
                    <div style="text-align:center; margin:.4rem 0 1.1rem;">
                        <span id="cobroMonto" style="font-family:var(--font-heading); font-size:2rem; color:var(--white);">${fmtMoneda(base, moneda)}</span>
                        <span id="cobroTachado" style="display:none; color:var(--text-muted); text-decoration:line-through; font-size:1rem; margin-left:.5rem;"></span>
                    </div>
                    <div style="display:grid; gap:.5rem; margin-bottom:1.1rem;">
                        <strong style="color:var(--white); font-size:.82rem;">¿Tenés un cupón?</strong>
                        <div style="display:flex; gap:.5rem;">
                            <input id="cobroCuponInput" type="text" placeholder="CÓDIGO" style="flex:1; min-width:0; padding:.55rem .7rem; border:1px solid rgba(255,255,255,.12); border-radius:9px; background:rgba(255,255,255,.045); color:var(--white); font-size:.86rem; text-transform:uppercase;">
                            <button type="button" id="cobroCuponBtn" class="btn btn-secondary" style="width:auto; margin:0; padding:.55rem .9rem;">Validar</button>
                        </div>
                        <span id="cobroCuponMsg" style="font-size:.74rem; display:none;"></span>
                    </div>
                    <button type="button" id="cobroPagarBtn" class="btn" style="width:100%; margin:0; min-height:50px; background:linear-gradient(135deg,var(--celeste),#22c55e); border:none; color:#fff; font-weight:bold;">Pagar con MercadoPago</button>
                </div>`;
            document.body.appendChild(overlay);

            overlay.addEventListener('click', (e) => { if (e.target === overlay) cerrarHojaCobro(); });
            overlay.querySelector('[data-cobro-close]').addEventListener('click', cerrarHojaCobro);

            const montoEl = overlay.querySelector('#cobroMonto');
            const tachadoEl = overlay.querySelector('#cobroTachado');
            const msgEl = overlay.querySelector('#cobroCuponMsg');
            const pagarBtn = overlay.querySelector('#cobroPagarBtn');

            // Validar cupón = preview read-only (NO consume; el consumo es al certificar).
            overlay.querySelector('#cobroCuponBtn').addEventListener('click', async () => {
                const codigo = normalizarCodigoCupon(overlay.querySelector('#cobroCuponInput').value);
                msgEl.style.display = 'block';
                const resetMonto = () => { montoEl.textContent = fmtMoneda(base, moneda); tachadoEl.style.display = 'none'; pagarBtn.textContent = 'Pagar con MercadoPago'; };
                if (!codigo) { msgEl.style.color = 'var(--text-muted)'; msgEl.textContent = 'Ingresá un código.'; cuponAplicado = null; resetMonto(); return; }
                const r = await validarCupon(codigo, grupo.group_id);
                if (r.error) { msgEl.style.color = '#ff9d9d'; msgEl.textContent = '⚠️ ' + traducirError(r.error); cuponAplicado = null; resetMonto(); return; }
                const v = r.data;
                if (!v?.valido) { msgEl.style.color = '#ff9d9d'; msgEl.textContent = '❌ ' + traducirMotivoCupon(v?.motivo); cuponAplicado = null; resetMonto(); return; }
                cuponAplicado = codigo;
                tachadoEl.style.display = 'inline'; tachadoEl.textContent = fmtMoneda(base, moneda);
                msgEl.style.color = '#7ab488';
                if (v.gratis) {
                    montoEl.textContent = '¡Gratis!';
                    msgEl.textContent = '🎁 Cupón 100% — certificás sin pagar.';
                    pagarBtn.textContent = 'Certificar gratis';
                } else {
                    montoEl.textContent = fmtMoneda(Math.floor(base * (100 - v.porcentaje) / 100), moneda);
                    msgEl.textContent = `✅ Cupón ${v.porcentaje}% aplicado.`;
                    pagarBtn.textContent = 'Pagar con MercadoPago';
                }
            });

            // Pagar / certificar: re-certifica con el cupón aplicado (congela la orden
            // descontada) y reacciona al retorno. 100% → certificado directo; parcial
            // o sin cupón → crear preferencia MP + redirigir al checkout.
            pagarBtn.addEventListener('click', async () => {
                pagarBtn.disabled = true; pagarBtn.style.opacity = '.6';
                const r = await certificarPacto(pacto.pacto_id, cuponAplicado || null);
                if (r.error) { alert('⚠️ ' + traducirError(r.error)); pagarBtn.disabled = false; pagarBtn.style.opacity = '1'; return; }
                if (r.data?.resultado === 'certificado') { // cupón 100% → directo, sin checkout
                    cerrarHojaCobro();
                    alert('¡El pacto está certificado!');
                    await renderPacto(codigo);
                    return;
                }
                const ordenId = r.data?.orden_id;
                try {
                    const resp = await fetch('/api/cobro/crear-pago', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ orden_id: ordenId }),
                    });
                    const pago = await resp.json().catch(() => ({}));
                    if (!resp.ok || !pago.init_point) {
                        alert('⚠️ No pudimos iniciar el pago. Probá de nuevo en un momento.');
                        pagarBtn.disabled = false; pagarBtn.style.opacity = '1'; return;
                    }
                    // Flag para el poll al volver (el webhook certifica server-side; puede demorar).
                    localStorage.setItem('cert_pago_pendiente', JSON.stringify({ codigo, group_id: grupo.group_id, ts: Date.now() }));
                    window.location.href = pago.init_point;
                } catch (e) {
                    alert('⚠️ Error de red al iniciar el pago.');
                    pagarBtn.disabled = false; pagarBtn.style.opacity = '1';
                }
            });
        };

        const btnCertificar = document.getElementById('btnCertificar');
        if (btnCertificar && canCertify) {
            btnCertificar.addEventListener('click', async () => {
                if (!confirm('Esta acción es irreversible. El pacto quedará certificado y no podrá editarse. Las sugerencias pendientes se cerrarán.')) return;
                // Descubrimiento de régimen SIN leer config_cobro: se reacciona al
                // retorno de certificar_pacto. OFF → certifica gratis directo;
                // ON → requiere_pago → abre la hoja de cobro.
                const r = await certificarPacto(pacto.pacto_id);
                if (r.error) { alert('⚠️ ' + traducirError(r.error)); return; }
                if (r.data?.resultado === 'certificado') {
                    alert('¡El pacto está certificado!');
                    await renderPacto(codigo);
                    return;
                }
                if (r.data?.resultado === 'requiere_pago') {
                    abrirHojaCobro(r.data);
                }
            });
        }

        const catSelect = document.getElementById('sugCategoria');
        if (catSelect) {
            catSelect.addEventListener('change', (e) => {
                document.getElementById('sugBloqueHonor').style.display = e.target.value === 'PREMIO_HONOR' ? 'block' : 'none';
                document.getElementById('sugBloquePozo').style.display = e.target.value === 'PREMIO_POZO' ? 'block' : 'none';
                document.getElementById('sugBloquePenalidad').style.display = e.target.value === 'PENALIDAD' ? 'block' : 'none';
                const textoExtra = document.getElementById('sugTextoExtra');
                if (textoExtra) textoExtra.style.display = e.target.value === 'PREMIO_POZO' ? 'none' : 'block';
            });
        }

        const sugAporteSelect = document.getElementById('sugValorAporte');
        if (sugAporteSelect) {
            sugAporteSelect.addEventListener('change', (e) => {
                document.getElementById('sugValorAporteOtro').style.display = e.target.value === 'otro' ? 'block' : 'none';
            });
            const inputSugAporte = document.getElementById('sugValorAporteOtro');
            if (inputSugAporte) {
                inputSugAporte.addEventListener('input', (e) => {
                    let val = e.target.value.replace(/\D/g, '');
                    e.target.value = val ? formatNumber(val) : '';
                });
            }
        }

        const btnSugerir = document.getElementById('btnSugerir');
        if (btnSugerir) {
            btnSugerir.addEventListener('click', async () => {
                const cat = document.getElementById('sugCategoria').value;
                if (!cat) { alert('Debes seleccionar una categoría.'); return; }

                if (!isEditor) {
                    const existing = pacto.sugerencias.find(s => s.user_id === jugadorActivo.id && s.categoria === cat);
                    if (existing) {
                        alert('Ya has enviado una sugerencia para esta categoría. Si quieres cambiarla, borra la anterior.');
                        return;
                    }
                }

                let valor = '';
                let extraText = document.getElementById('sugTextoExtra').value.trim();
                if (cat === 'PREMIO_POZO') extraText = ''; // Oculto y no aplica para pozo
                let text = '';

                if (cat === 'PREMIO_HONOR') {
                    valor = document.getElementById('sugValorPremioHonor').value;
                    if (!valor) { alert('Debes seleccionar una opción.'); return; }
                    const textFinal = extraText ? `${valor} (${extraText})` : valor;
                    text = `Sugerencia de Premio (Honor): ${textFinal}`;
                } else if (cat === 'PREMIO_POZO') {
                    let aporte = document.getElementById('sugValorAporte').value;
                    if (aporte === 'otro') {
                        aporte = document.getElementById('sugValorAporteOtro').value.trim();
                        if (!aporte) { alert('Debes ingresar el monto del aporte.'); return; }
                    }
                    const reparto = document.getElementById('sugValorPremioPozo').value;
                    if (!aporte || !reparto) { alert('Debes seleccionar el aporte y el reparto.'); return; }
                    valor = JSON.stringify({ aporte, reparto });
                    text = `Sugerencia de Premio (Pozo): Aporte: ${aporte} | Reparto: ${reparto}`;
                } else if (cat === 'PENALIDAD') {
                    valor = document.getElementById('sugValorPenalidad').value;
                    if (!valor) { alert('Debes seleccionar una opción.'); return; }
                    const textFinal = extraText ? `${valor} (${extraText})` : valor;
                    text = `Sugerencia de Castigo: ${textFinal}`;
                }

                const r = await crearPropuestaPacto(pacto.pacto_id, jugadorActivo.id, cat, {
                    categoria_val: valor,
                    texto_extra: extraText,
                    texto: text,
                });
                if (r.error) { alert('⚠️ ' + traducirError(r.error)); return; }
                await renderPacto(codigo);
            });
        }

        document.querySelectorAll('.btn-sugerencia-aplicar').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                if (!confirm('Aplicar esta sugerencia actualizará la configuración automáticamente y reiniciará las firmas. ¿Confirmar?')) return;
                const id = e.target.getAttribute('data-id');
                const sug = pacto.sugerencias.find(s => s.id === id);
                if (!sug) return;

                let parsedValue = sug.categoria_val;
                let parsedExtra = sug.texto_extra;

                if (!parsedValue) {
                    const rawText = sug.texto.replace(/^Sugerencia de (?:Premio|Castigo): /, '');
                    const match = rawText.match(/^(.*?)(?: \((.*)\))?$/);
                    if (match) {
                        parsedValue = match[1];
                        parsedExtra = match[2] || '';
                    } else {
                        parsedValue = rawText;
                        parsedExtra = '';
                    }
                }

                const nuevoConfig = { ...config };
                let nuevoTipo = pacto.tipo;

                if (sug.categoria === 'PREMIO_HONOR') {
                    nuevoTipo = 'honor';
                    nuevoConfig.premioHonor = parsedValue;
                    nuevoConfig.premioHonorOtro = parsedExtra;
                } else if (sug.categoria === 'PREMIO_POZO') {
                    nuevoTipo = 'pozo';
                    try {
                        const pozoData = JSON.parse(parsedValue);
                        nuevoConfig.aporte = pozoData.aporte;
                        nuevoConfig.premioPozo = pozoData.reparto;
                        nuevoConfig.aporteOtro = parsedExtra; // Si eligieron otro y agregaron comentario
                    } catch (err) {
                        // Fallback for old suggestions before this update
                        nuevoConfig.premioPozo = parsedValue;
                    }
                } else if (sug.categoria === 'PENALIDAD') {
                    nuevoConfig.penalidad = parsedValue;
                    nuevoConfig.penalidadOtro = parsedExtra;
                } else if (sug.categoria === 'PREMIO') {
                    // Fallback for old suggestions
                    if (pacto.tipo === 'pozo') {
                        nuevoConfig.premioPozo = parsedValue;
                    } else {
                        nuevoConfig.premioHonor = parsedValue;
                        nuevoConfig.premioHonorOtro = parsedExtra;
                    }
                }

                const r = await guardarPacto(grupo.group_id, nombrePacto, nuevoTipo, nuevoConfig);
                if (r.error) { alert('⚠️ ' + traducirError(r.error)); return; }
                const r2 = await actualizarEstadoPropuesta(id, 'aplicada');
                if (r2.error) { alert('⚠️ ' + traducirError(r2.error)); return; }
                await renderPacto(codigo);
            });
        });

        document.querySelectorAll('.btn-sugerencia-ignorar').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const id = e.target.getAttribute('data-id');
                const r = await actualizarEstadoPropuesta(id, 'descartada');
                if (r.error) { alert('⚠️ ' + traducirError(r.error)); return; }
                await renderPacto(codigo);
            });
        });

        document.querySelectorAll('.btn-sugerencia-borrar').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                if (!confirm('¿Seguro que deseas borrar tu sugerencia?')) return;
                const id = e.target.getAttribute('data-id');
                const r = await borrarPropuesta(id);
                if (r.error) { alert('⚠️ ' + traducirError(r.error)); return; }
                await renderPacto(codigo);
            });
        });

        const toggleDelegar = document.getElementById('toggleDelegar');
        const panelDelegar = document.getElementById('panelDelegar');
        if (toggleDelegar && panelDelegar) {
            toggleDelegar.addEventListener('click', () => {
                panelDelegar.style.display = panelDelegar.style.display === 'none' ? 'block' : 'none';
            });
        }

        const btnInlineDelegarConfirm = document.getElementById('btnInlineDelegarConfirm');
        if (btnInlineDelegarConfirm) {
            btnInlineDelegarConfirm.addEventListener('click', async () => {
                const nuevoEditorId = document.getElementById('inlineDelegarSelect').value;
                if (!nuevoEditorId) return;
                const nuevoEditor = grupo.jugadores.find(j => String(j.id) === nuevoEditorId);
                if (confirm(`Transferirás el control de edición a ${nuevoEditor.apodo}. ¿Confirmar?`)) {
                    const r = await delegarPacto(pacto.pacto_id, nuevoEditorId);
                    if (r.error) { alert('⚠️ ' + traducirError(r.error)); return; }
                    await renderPacto(codigo);
                }
            });
        }
    }

    const btnTomarControl = document.getElementById('btnTomarControl');
    if (btnTomarControl) {
        btnTomarControl.addEventListener('click', async () => {
            if (confirm('Tomarás el control de edición. Se creará una nueva versión del pacto y se reiniciarán las firmas. ¿Confirmar?')) {
                const r = await tomarControlPacto(pacto.pacto_id);
                if (r.error) { alert('⚠️ ' + traducirError(r.error)); return; }
                await renderPacto(codigo);
            }
        });
    }
};

if (typeof window !== 'undefined') {
  window.renderPacto = renderPacto;

  // C3 — al volver de MercadoPago, confirmar la certificación.
  // El webhook (C2) certifica server-side; suele ser ~instantáneo pero puede
  // demorar. Mientras tanto, mostramos un overlay "Procesando tu pago…" (para
  // que el pagador NO vea el pacto "sin definir") y polleamos el estado del
  // pacto hasta ~60s, re-renderizando EN EL LUGAR cuando certifica. El chequeo
  // se dispara en `load` Y en `pageshow`: este último cubre el regreso por el
  // botón Atrás / bfcache, donde `load` NO se vuelve a disparar (causa real de
  // la vista que quedaba congelada). NO lee config_cobro.
  const CERT_FLAG = 'cert_pago_pendiente';
  const CERT_OVERLAY_ID = 'cert-procesando-overlay';

  // Lee el retorno de MercadoPago del query string. Devuelve 'pagado' (pago real:
  // status approved/pending/in_process/authorized o payment_id real), 'cancelado'
  // (volvió SIN pagar: status null/rejected/cancelled/failure sin payment_id real)
  // o 'ninguno'. Inline (idéntica en main.js) para no sumar import al flujo de pago.
  const leerRetornoMP = () => {
    let sp;
    try { sp = new URLSearchParams(window.location.search || ''); }
    catch { return 'ninguno'; }
    const CLAVES = ['status', 'collection_status', 'payment_id', 'collection_id', 'preference_id'];
    if (!CLAVES.some((k) => sp.has(k))) return 'ninguno';
    const status = (sp.get('status') || sp.get('collection_status') || '').toLowerCase();
    const paymentId = sp.get('payment_id') || sp.get('collection_id') || '';
    const real = (v) => Boolean(v) && v !== 'null';
    const PAGANDO = ['approved', 'pending', 'in_process', 'authorized'];
    const CANCELADO = ['rejected', 'cancelled', 'refunded', 'charged_back', 'failure', 'null'];
    if (PAGANDO.includes(status)) return 'pagado';
    if (CANCELADO.includes(status)) return 'cancelado';
    return real(paymentId) ? 'pagado' : 'cancelado';
  };

  const ensureSpinKeyframes = () => {
    if (document.getElementById('cert-spin-style')) return;
    const st = document.createElement('style');
    st.id = 'cert-spin-style';
    st.textContent = '@keyframes certspin{to{transform:rotate(360deg)}}';
    document.head.appendChild(st);
  };
  const mostrarProcesando = (mensaje, conReintento) => {
    ensureSpinKeyframes();
    let ov = document.getElementById(CERT_OVERLAY_ID);
    if (!ov) {
      ov = document.createElement('div');
      ov.id = CERT_OVERLAY_ID;
      ov.style.cssText = 'position:fixed;inset:0;z-index:120;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1.1rem;padding:2rem;text-align:center;background:rgba(9,14,23,.94);color:#fff;';
      document.body.appendChild(ov);
    }
    const spinner = conReintento ? '' : '<div style="width:42px;height:42px;border:3px solid rgba(255,255,255,.22);border-top-color:#fff;border-radius:50%;animation:certspin 1s linear infinite;"></div>';
    const boton = conReintento ? '<button id="cert-reintentar" style="margin-top:.4rem;padding:.65rem 1.5rem;border-radius:12px;border:0;background:#fff;color:#090e17;font-weight:700;cursor:pointer;">Reintentar</button>' : '';
    ov.innerHTML = `${spinner}<div style="font-size:1.05rem;font-weight:600;max-width:18rem;line-height:1.4;">${mensaje}</div>${boton}`;
    return ov;
  };
  const ocultarProcesando = () => {
    const ov = document.getElementById(CERT_OVERLAY_ID);
    if (ov) ov.remove();
  };

  let chequeando = false;
  const chequearPagoPendiente = async () => {
    const raw = localStorage.getItem(CERT_FLAG);
    if (!raw) return;
    let info;
    try { info = JSON.parse(raw); } catch { localStorage.removeItem(CERT_FLAG); return; }
    if (!info?.group_id) { localStorage.removeItem(CERT_FLAG); return; }
    if (info.ts && Date.now() - info.ts > 600000) { localStorage.removeItem(CERT_FLAG); ocultarProcesando(); return; } // >10 min: descartar
    // Defensivo: el overlay solo se muestra con SEÑAL POSITIVA de pago. Sin ella
    // —status=null/cancelado, O bfcache del botón Atrás que restaura SIN params—
    // limpiar el flag y NO mostrar overlay (cubre el auto-disparo por load/pageshow/
    // hashchange). Sin pago no certifica, así que el poll solo colgaría la vista.
    if (leerRetornoMP() !== 'pagado') { localStorage.removeItem(CERT_FLAG); ocultarProcesando(); return; }
    if (chequeando) return; // un poll ya en curso (load + pageshow pueden solaparse)
    chequeando = true;
    mostrarProcesando('Procesando tu pago…');
    try {
      const deadline = Date.now() + 60000; // ~60s de ventana, cubre el lag del webhook
      while (Date.now() < deadline) {
        let pacto = null;
        try { pacto = await getPactoByGroup(info.group_id); } catch { /* red caída: reintenta en el próximo tick */ }
        if (pacto?.estado === 'certificado') {
          localStorage.removeItem(CERT_FLAG);
          ocultarProcesando();
          // Invalidar el pacto cacheado del home → renderGrupo re-fetchea fresco
          // (mata el "Pacto sin definir" stale al volver al home, grupo.js:637).
          try { const g = getDB().grupos[info.codigo]; if (g) g.pacto = undefined; } catch { /* cache no listo: no-op */ }
          if (info.codigo) { await renderPacto(info.codigo); } // re-render en el lugar (sin recargar)
          else { window.location.reload(); } // fallback defensivo si no vino el código
          return;
        }
        await new Promise((res) => window.setTimeout(res, 2000)); // backoff suave
      }
      // Timeout: dejamos el flag (el próximo load/pageshow reintenta) y damos
      // feedback con botón Reintentar (en vez de un warn silencioso).
      const ov = mostrarProcesando('Tu pago se está acreditando, puede tardar un momento.', true);
      const btn = ov.querySelector('#cert-reintentar');
      if (btn) btn.addEventListener('click', () => { ocultarProcesando(); chequearPagoPendiente(); });
    } finally {
      chequeando = false;
    }
  };

  // Expuesto para smoke manual sin pagar: setear cert_pago_pendiente y llamar.
  window.__pmCertCheck = chequearPagoPendiente;

  // Auto-disparo SOLO estando en la vista del pacto (#/pacto/...). MP devuelve a
  // la raíz; el hook de arranque (main.js) rutea a /pacto/CODE con el grupo ya en
  // cache → el poll corre en contexto y no rebota a /ingreso por la carrera de
  // hidratación. Para smoke manual queda window.__pmCertCheck (sin gate).
  const enVistaPacto = () => String(window.location.hash || '').startsWith('#/pacto/');
  const autoChequear = () => { if (enVistaPacto()) window.setTimeout(chequearPagoPendiente, 400); };
  if (typeof window.addEventListener === 'function') {
    window.addEventListener('load', autoChequear);
    // pageshow cubre el regreso por botón Atrás / bfcache (load no se redispara).
    window.addEventListener('pageshow', autoChequear);
    // hashchange: el hook de arranque navega a /pacto/CODE al volver de MP.
    window.addEventListener('hashchange', autoChequear);
  }
}
